import {useMyTasksSummary} from '@domain/tasks'
import {DataContentWrapper} from '@hconnect/common/containers'
import {
  CardBox,
  CardLinkWrapper,
  SummaryScores,
  type SummaryScoreProps
} from '@hconnect/uikit/src/lib2'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {spawnErrorToast} from '@hm/fundamental'
import {CalendarTodayOutlined} from '@mui/icons-material'
import {Box, Chip, SxProps, Theme, Typography} from '@mui/material'
import moment from 'moment-timezone'
import {useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {TASKS_WIDGET_NAMESPACE} from '../consts'
type Props = {
  plantId: string
  cardBoxSx?: SxProps<Theme>
  placeholderWrapperSx?: SxProps<Theme>
  myTasksHref?: string
}

export const MyTasksSummaryContainer: React.FC<Props> = ({
  plantId,
  cardBoxSx,
  placeholderWrapperSx,
  myTasksHref
}) => {
  const {t} = useTranslation(TASKS_WIDGET_NAMESPACE)
  const timezone = useUpmPlantTimezone(plantId)
  const startOfToday = moment.utc().tz(timezone).startOf('day')
  const endOfToday = startOfToday.clone().endOf('day')

  const notifyFailedToFetchEvents = useCallback(
    () => spawnErrorToast(t('myTasksSummary.message.failedToFetchMyTasksSummary')),
    [t]
  )

  const timeRange = {
    startDate: startOfToday.toISOString(),
    endDate: endOfToday.toISOString()
  }

  const {data, isLoading, isError, isRefetching, refetch} = useMyTasksSummary(plantId, timeRange, {
    onError: notifyFailedToFetchEvents
  })

  const summaryScoreItems: SummaryScoreProps[] = data
    ? [
        {
          label: t('myTasksSummary.label.open'),
          value: data.open,
          valueVariant: 'neutral',
          tooltip: t('myTasksSummary.tooltip.open'),
          showTooltip: data.open > 0,
          dataTestId: 'my-tasks-open'
        },
        {
          label: t('myTasksSummary.label.done'),
          value: data.done,
          valueVariant: 'neutral',
          showTooltip: false,
          dataTestId: 'my-tasks-done'
        },
        {
          label: t('myTasksSummary.label.overdue'),
          value: data.overdue,
          valueVariant: data.overdue > 0 ? 'negative' : 'neutral',
          tooltip: t('myTasksSummary.tooltip.overdue'),
          showTooltip: data.overdue > 0,
          dataTestId: 'my-tasks-overdue'
        }
      ]
    : []

  return (
    <CardLinkWrapper href={myTasksHref} data-test-id="my-tasks-card-link">
      <CardBox sx={cardBoxSx} data-test-id="my-tasks-summary-card">
        <Box display="flex" flexDirection="row">
          <Box flex={1}>
            <Typography variant="h3" mb={2}>
              {t('myTasksSummary.cardTitle')}
            </Typography>
          </Box>
          <Chip
            icon={<CalendarTodayOutlined />}
            label={t('label.today')}
            size="small"
            sx={{mx: 1}}
          />
        </Box>
        <DataContentWrapper
          showLoader={isLoading}
          isError={isError}
          isRefetching={isRefetching}
          data={data}
          retryFunction={() => void refetch()}
          progressSx={{color: 'primary.main'}}
          placeholderWrapperSx={placeholderWrapperSx}
        >
          {data && <SummaryScores items={summaryScoreItems} />}
        </DataContentWrapper>
      </CardBox>
    </CardLinkWrapper>
  )
}
